import { api, BASE_URL } from './api';

export interface AskRequest {
  question: string;
  conversationId?: string;
  modelOverride?: string;
}

export interface AskResponse {
  conversationId: string;
  messageId: string;
  answer: string;
  citedChunkIds: string[];
  retrievedChunkIds: string[];
  model: string;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
  relatedQuestions?: string[];
}

export interface ConversationSummary {
  id: string;
  workspace_id: string;
  user_id: string;
  title: string | null;
  created_at: string;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  cited_chunk_ids: string[] | null;
  model: string | null;
  cost_cents: number | null;
  created_at: string;
}

export interface ChunkDetail {
  id: string;
  documentId: string;
  page: number | null;
  headingPath: string[];
  text: string;
  language: string | null;
}

export interface AskStreamHandlers {
  /** Fired once, before tokens, with the retrieved chunk ids + model. */
  onMeta?: (meta: { conversationId: string; retrievedChunkIds: string[]; model: string }) => void;
  /** Fired for each streamed text fragment. */
  onToken?: (text: string) => void;
  /** Fired once when the answer is complete, with the final persisted result. */
  onDone?: (resp: AskResponse) => void;
  /** Fired on a server- or transport-level error. */
  onError?: (message: string) => void;
}

export const askApi = {
  async ask(wsId: string, req: AskRequest): Promise<AskResponse> {
    const r = await api.post<AskResponse>(`/api/workspaces/${wsId}/ask`, req);
    return r.data;
  },

  /**
   * Streaming Ask over SSE. Uses fetch (not axios) so we can read the body as
   * a stream; cookies ride along via credentials:'include'. Returns an abort
   * function the caller can use to cancel an in-flight answer.
   */
  askStream(wsId: string, req: AskRequest, handlers: AskStreamHandlers): () => void {
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/workspaces/${wsId}/ask/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(req),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) {
          handlers.onError?.(`Ask failed (${res.status})`);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Parse the SSE framing: events are separated by a blank line, each
        // carrying one `data: <json>` line.
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sep: number;
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);

            const dataLine = rawEvent
              .split('\n')
              .find((l) => l.startsWith('data:'));
            if (!dataLine) continue;
            const json = dataLine.slice(5).trim();
            if (!json) continue;

            let ev: any;
            try {
              ev = JSON.parse(json);
            } catch {
              continue;
            }

            if (ev.type === 'meta') {
              handlers.onMeta?.({
                conversationId: ev.conversationId,
                retrievedChunkIds: ev.retrievedChunkIds ?? [],
                model: ev.model,
              });
            } else if (ev.type === 'token') {
              handlers.onToken?.(ev.text ?? '');
            } else if (ev.type === 'done') {
              handlers.onDone?.(ev as AskResponse);
            } else if (ev.type === 'error') {
              handlers.onError?.(ev.message ?? 'Ask failed');
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        handlers.onError?.((err as Error).message || 'Ask failed');
      }
    })();

    return () => controller.abort();
  },
  async listConversations(wsId: string): Promise<ConversationSummary[]> {
    const r = await api.get<ConversationSummary[]>(`/api/workspaces/${wsId}/conversations`);
    return r.data;
  },
  async getConversation(wsId: string, id: string): Promise<{ conversation: ConversationSummary; messages: ConversationMessage[] }> {
    const r = await api.get<{ conversation: ConversationSummary; messages: ConversationMessage[] }>(
      `/api/workspaces/${wsId}/conversations/${id}`,
    );
    return r.data;
  },
  async getChunk(wsId: string, id: string): Promise<ChunkDetail | { error: string }> {
    const r = await api.get<ChunkDetail | { error: string }>(`/api/workspaces/${wsId}/chunks/${id}`);
    return r.data;
  },
};
